import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import type { TranslationKey } from '@/i18n';
import type { WorkspaceModule } from './types';
import { ModuleDataView } from './components/ModuleDataView';

interface WorkspaceModulePageProps {
  modules: WorkspaceModule[];
  basePath: string;
  loadModule: (
    key: string,
    params: { tab: string; filter: string; search: string; candidateId?: string },
  ) => Promise<Record<string, unknown>>;
}

export const WorkspaceModulePage = ({
  modules,
  basePath,
  loadModule,
}: WorkspaceModulePageProps) => {
  const { moduleKey } = useParams<{ moduleKey: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const module = modules.find((item) => item.key === moduleKey);
  const candidateId = searchParams.get('candidate') ?? undefined;
  const [tab, setTab] = useState(module?.tabs[0] ?? 'registry');
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!moduleKey) return;
    setLoading(true);
    try {
      const payload = await loadModule(moduleKey, { tab, filter, search, candidateId });
      setData(payload);
    } finally {
      setLoading(false);
    }
  }, [candidateId, filter, loadModule, moduleKey, search, tab]);

  useEffect(() => {
    if (!module) {
      navigate(basePath, { replace: true });
      return;
    }
    if (!candidateId) {
      setTab(module.tabs[0] ?? 'registry');
    }
  }, [basePath, candidateId, module, navigate]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (!module) return null;

  return (
    <section className="dashboard workspace-module">
      <div className="dashboard-inner">
        <header className="workspace-module-head">
          <div>
            <Link
              to={candidateId ? `${basePath}/${module.key}` : basePath}
              className="workspace-back"
            >
              {t('common.back')}
            </Link>
            <h2>{module.label[locale]}</h2>
            <p>{module.description[locale]}</p>
          </div>
        </header>

        {!candidateId && module.tabs.length > 1 && (
          <div className="tab-bar" role="tablist">
            {module.tabs.map((item) => (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={tab === item}
                onClick={() => setTab(item)}
              >
                {t(`workspace.tab.${item}` as TranslationKey)}
              </button>
            ))}
          </div>
        )}

        {!candidateId &&
          (module.key === 'requests' ||
            module.key === 'orders' ||
            module.key === 'supervision' ||
            module.key === 'personal_requests' ||
            module.key === 'moderation' ||
            module.key === 'publications' ||
            module.key === 'doctoral_process' ||
            module.key === 'monitoring') && (
            <div className="workspace-filters">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('common.search')}
              />
              {(module.key === 'requests' || module.key === 'orders') && (
                <select value={filter} onChange={(event) => setFilter(event.target.value)}>
                  <option value="ALL">{t('common.all')}</option>
                  <option value="IN_PROGRESS">В работе</option>
                  <option value="NEEDS_CLARIFICATION">Нужно уточнение</option>
                  <option value="SERVICE">Услуга</option>
                  <option value="READY_ARTICLE">Готовая статья</option>
                </select>
              )}
              <button type="button" onClick={() => void fetchData()}>
                {t('common.search')}
              </button>
            </div>
          )}

        {loading ? (
          <p className="workspace-loading">{t('common.loading')}</p>
        ) : (
          <ModuleDataView moduleKey={module.key} tab={tab} data={data ?? {}} basePath={basePath} />
        )}
      </div>
    </section>
  );
};
